import { useId, useRef, useState, type FormEvent } from 'react';
import type { FilmFormat, FilmProcess, FilmStock, FilmStockInput } from '../api/filmStocks';
import { FORMATS, PROCESS_LABELS, stockLabel } from '../utils/filmStocks';
import styles from './FilmStockForm.module.css';

const MAX_ISO = 25600;

export interface FilmStockFormProps {
  /** The stock being edited; a new stock when absent. */
  stock?: FilmStock;
  /** Starting values for a new stock (say, from what was typed in the picker). */
  initial?: Partial<FilmStockInput>;
  /** Existing brands, suggested as the brand is typed. */
  brands: string[];
  saving: boolean;
  /** The server's refusal, if the last save failed. */
  error?: string | null;
  /** The stock that already has this name, after a 409. */
  duplicate?: FilmStock | null;
  onSubmit: (input: FilmStockInput) => void;
  onCancel: () => void;
  /** Switch to editing the stock that already has this name. */
  onOpenDuplicate?: (stock: FilmStock) => void;
  /** What that offer says: "Edit Kodak Portra 400 instead" by default. */
  duplicateActionLabel?: (label: string) => string;
}

interface Errors {
  brand?: string;
  name?: string;
  iso?: string;
}

/** Add or edit one film stock. */
export function FilmStockForm({
  stock,
  initial,
  brands,
  saving,
  error,
  duplicate,
  onSubmit,
  onCancel,
  onOpenDuplicate,
  duplicateActionLabel = (label) => `Edit ${label} instead`,
}: FilmStockFormProps) {
  const id = useId();
  const start = stock ?? initial ?? {};
  const [brand, setBrand] = useState(start.brand ?? '');
  const [name, setName] = useState(start.name ?? '');
  const [iso, setIso] = useState(start.box_iso ? String(start.box_iso) : '');
  const [process, setProcess] = useState<FilmProcess>(start.process ?? 'c41');
  const [formats, setFormats] = useState<FilmFormat[]>(start.formats ?? ['35mm']);
  const [aliases, setAliases] = useState((start.aliases ?? []).join('\n'));
  const [active, setActive] = useState(stock?.active ?? true);
  const [errors, setErrors] = useState<Errors>({});
  const brandRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const isoRef = useRef<HTMLInputElement>(null);

  function validate(): Errors {
    const found: Errors = {};
    if (!brand.trim()) found.brand = 'Enter the brand';
    if (!name.trim()) found.name = "Enter the stock's name";
    if (iso.trim()) {
      const n = Number(iso);
      if (!Number.isInteger(n) || n < 1 || n > MAX_ISO) found.iso = `Enter a whole number from 1 to ${MAX_ISO}, or leave it blank`;
    }
    return found;
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // Opened from another form (booking in, a roll's details), this submit
    // would otherwise bubble on and submit that form too.
    e.stopPropagation();
    const found = validate();
    setErrors(found);
    const first = found.brand ? brandRef : found.name ? nameRef : found.iso ? isoRef : null;
    if (first) {
      first.current?.focus();
      return;
    }
    onSubmit({
      brand: brand.trim(),
      name: name.trim(),
      box_iso: iso.trim() ? Number(iso) : null,
      process,
      formats: FORMATS.filter((f) => formats.includes(f)),
      aliases: aliases.split('\n').map((a) => a.trim()).filter(Boolean),
      ...(stock ? { active } : {}),
    });
  }

  const errorId = (field: keyof Errors) => `${id}-${field}-error`;
  const shopFormats = stock?.shop_products.map((p) => p.format) ?? [];

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.field}>
        <label htmlFor={`${id}-brand`} className={styles.label}>
          Brand
        </label>
        <input
          ref={brandRef}
          id={`${id}-brand`}
          className={styles.input}
          list={`${id}-brands`}
          autoComplete="off"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          aria-invalid={!!errors.brand}
          aria-describedby={errors.brand ? errorId('brand') : undefined}
        />
        <datalist id={`${id}-brands`}>
          {brands.map((b) => (
            <option key={b} value={b} />
          ))}
        </datalist>
        {errors.brand && (
          <p id={errorId('brand')} className={styles.fieldError}>
            {errors.brand}
          </p>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor={`${id}-name`} className={styles.label}>
          Name
        </label>
        <input
          ref={nameRef}
          id={`${id}-name`}
          className={styles.input}
          autoComplete="off"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={!!errors.name}
          aria-describedby={[`${id}-name-hint`, errors.name ? errorId('name') : ''].filter(Boolean).join(' ')}
        />
        <p id={`${id}-name-hint`} className={styles.hint}>
          As on the box, without the brand: “Portra 400”.
        </p>
        {errors.name && (
          <p id={errorId('name')} className={styles.fieldError}>
            {errors.name}
          </p>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor={`${id}-iso`} className={styles.label}>
          Box ISO <span className={styles.optional}>(optional)</span>
        </label>
        <input
          ref={isoRef}
          id={`${id}-iso`}
          className={`${styles.input} ${styles.short}`}
          inputMode="numeric"
          value={iso}
          onChange={(e) => setIso(e.target.value)}
          aria-invalid={!!errors.iso}
          aria-describedby={[`${id}-iso-hint`, errors.iso ? errorId('iso') : ''].filter(Boolean).join(' ')}
        />
        <p id={`${id}-iso-hint`} className={styles.hint}>
          Leave it blank for a range, such as 100–400.
        </p>
        {errors.iso && (
          <p id={errorId('iso')} className={styles.fieldError}>
            {errors.iso}
          </p>
        )}
      </div>

      <fieldset className={styles.fieldset}>
        <legend className={styles.label}>Process</legend>
        <div className={styles.choices}>
          {(Object.keys(PROCESS_LABELS) as FilmProcess[]).map((p) => (
            <label key={p} className={styles.choice}>
              <input type="radio" name={`${id}-process`} value={p} checked={process === p} onChange={() => setProcess(p)} />
              {PROCESS_LABELS[p]}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.label}>Formats</legend>
        <div className={styles.choices}>
          {FORMATS.map((f) => (
            <label key={f} className={styles.choice}>
              <input
                type="checkbox"
                checked={formats.includes(f)}
                onChange={(e) => setFormats((now) => (e.target.checked ? [...now, f] : now.filter((x) => x !== f)))}
              />
              {f}
            </label>
          ))}
        </div>
      </fieldset>

      <div className={styles.field}>
        <label htmlFor={`${id}-aliases`} className={styles.label}>
          Other names <span className={styles.optional}>(optional)</span>
        </label>
        <textarea
          id={`${id}-aliases`}
          className={styles.input}
          rows={3}
          value={aliases}
          onChange={(e) => setAliases(e.target.value)}
          aria-describedby={`${id}-aliases-hint`}
        />
        <p id={`${id}-aliases-hint`} className={styles.hint}>
          One per line: earlier names, rebrands, how customers write it. The picker finds the stock by these too.
        </p>
      </div>

      {stock && (
        <>
          <label className={styles.choice}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Show in the picker
          </label>
          <p className={styles.hint}>
            Turning a stock off hides it from booking in. Rolls already on it keep it.
            {shopFormats.length > 0 && ` The shop sells it in ${shopFormats.join(' and ')}.`}
          </p>
        </>
      )}

      {error && (
        <div role="alert" className={styles.formError}>
          <p>{error}</p>
          {duplicate && onOpenDuplicate && (
            <button type="button" className={styles.linkButton} onClick={() => onOpenDuplicate(duplicate)}>
              {duplicateActionLabel(stockLabel(duplicate))}
            </button>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.secondaryBtn} onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className={styles.primaryBtn} disabled={saving}>
          {saving ? 'Saving…' : stock ? 'Save changes' : 'Add stock'}
        </button>
      </div>
    </form>
  );
}
